import { useState } from "react";
import axios from "axios";
import { ShoppingBasket, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Fresh milk", "Local honey", "Bonfire arrangement", "Bicycle rental",
  "Yoga mat", "Board games", "Vegetable box", "Barbecue set",
];

/**
 * Wishlist card shown on the guest portal after RSVP is confirmed. Lets the
 * guest send a short pre-order list to their host before arrival.
 */
export function WishlistCard({ booking, email, token, initial }) {
  const existing = initial[0]?.items || [];
  const [items, setItems] = useState(existing.length ? existing : [""]);
  const [saved, setSaved] = useState(existing);
  const [busy, setBusy] = useState(false);

  const setAt = (i, v) => setItems((arr) => arr.map((x, k) => k === i ? v : x));
  const add = () => setItems((arr) => [...arr, ""]);
  const remove = (i) => setItems((arr) => arr.filter((_, k) => k !== i));

  const submit = async () => {
    setBusy(true);
    try {
      const clean = items.map((s) => s.trim()).filter(Boolean);
      const { data } = await axios.post(`${API}/guest-portal/wishlist`,
        { booking, email, token, items: clean });
      setSaved(clean);
      alert(`Saved ${data.count} item(s). Your host has been notified.`);
    } catch (e) {
      alert(e.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 p-5 border border-border rounded-lg bg-card" data-testid="wishlist-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          <ShoppingBasket size={12} /> Pre-order for your stay
        </div>
        {saved.length > 0 && <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">{saved.length} sent</span>}
      </div>
      <p className="text-xs text-muted-foreground mb-3">Groceries, activities or a bonfire evening — let the host know before you arrive.</p>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex gap-2">
            <input list="wishlist-suggestions" value={it} onChange={(e) => setAt(i, e.target.value)}
                   placeholder="e.g. Fresh milk, bonfire"
                   className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm"
                   data-testid={`wishlist-item-${i}`} />
            {items.length > 1 && (
              <button type="button" onClick={() => remove(i)} className="h-9 w-9 rounded-md border border-border flex items-center justify-center hover:bg-secondary/40" aria-label="Remove">
                <X size={14} />
              </button>
            )}
          </div>
        ))}
        <datalist id="wishlist-suggestions">
          {SUGGESTIONS.map((s) => <option key={s} value={s} />)}
        </datalist>
      </div>
      <div className="flex gap-2 mt-3">
        {items.length < 20 && (
          <button type="button" onClick={add} className="text-xs inline-flex items-center gap-1 text-primary hover:underline" data-testid="wishlist-add-btn">
            <Plus size={12} /> Add another
          </button>
        )}
        <div className="flex-1" />
        <button type="button" onClick={submit} disabled={busy}
                className="h-9 px-4 rounded-md bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-60"
                data-testid="wishlist-submit-btn">
          {busy ? "Sending…" : "Send to host"}
        </button>
      </div>
    </div>
  );
}
